import React, { useEffect, useState } from 'react';
import { submitFormData } from '../../../../pages/api';

const BarcodeRFID = ({ globalFields, formData, onFormDataChange }) => {
    const { barcodeRFIDData = [], observations = [], nonConformance = '' } = formData;
    const [validationErrors, setValidationErrors] = useState([]);

    useEffect(() => {
        const hasFormData =
            barcodeRFIDData.length > 0 || observations.length > 0 || nonConformance.trim() !== '';

        if (!hasFormData) {
            const metadataFromSession = JSON.parse(sessionStorage.getItem('metadata'));
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

            let prefilledBarcodeRFIDData = [];
            let prefilledObservations = [];
            let prefilledNonConformance = '';

            if (
                metadataFromSession &&
                metadataFromSession.pre &&
                metadataFromSession.pre.barCodeRFID_pre
            ) {
                prefilledBarcodeRFIDData = metadataFromSession.pre.barCodeRFID_pre;
                prefilledObservations = prefilledBarcodeRFIDData.map(() => ({
                    observation1: '',
                    observation2: '',
                }));
            }

            if (
                inspectionData &&
                inspectionData.PreLamination &&
                inspectionData.PreLamination.barCodeRFID_pre
            ) {
                const { barcodeRFIDData: inspectionBarcodeRFIDData, nonConformance: inspectionNonConformance } =
                    inspectionData.PreLamination.barCodeRFID_pre;

                prefilledBarcodeRFIDData = inspectionBarcodeRFIDData || [];
                prefilledObservations = inspectionBarcodeRFIDData.map((item) => ({
                    observation1: item.observation1 || '',
                    observation2: item.observation2 || '',
                }));
                prefilledNonConformance = inspectionNonConformance || '';
            }

            if (
                prefilledBarcodeRFIDData.length > 0 ||
                prefilledObservations.length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    barcodeRFIDData: prefilledBarcodeRFIDData,
                    observations: prefilledObservations,
                    nonConformance: prefilledNonConformance,
                });
            }
        }

        // Ensure observations array matches barcodeRFIDData length
        if (barcodeRFIDData.length > 0 && observations.length !== barcodeRFIDData.length) {
            const newObservations = barcodeRFIDData.map((_, index) => ({
                observation1: observations[index]?.observation1 || '',
                observation2: observations[index]?.observation2 || '',
            }));
            onFormDataChange({
                ...formData,
                observations: newObservations,
            });
        }
    }, [formData, barcodeRFIDData, observations, onFormDataChange]);

    const handleObservationChange = (index, field, value) => {
        const updatedObservations = [...observations];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, observations: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent default form submission

        // Validate Observation 1 fields
        const errors = observations.map(
            (obs) => !obs.observation1 || obs.observation1.trim() === ''
        );

        if (errors.some((error) => error)) {
            setValidationErrors(errors);
            alert('Please fill in all Observation 1 fields.');
            return;
        }

        setValidationErrors([]);

        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            PreLamination: {
                barCodeRFID_pre: {
                    barcodeRFIDData: barcodeRFIDData.map((item, index) => ({
                        ...item,
                        observation1: observations[index]?.observation1 || '',
                        observation2: observations[index]?.observation2 || '',
                    })),
                    nonConformance,
                },
            },
        };

        try {
            await submitFormData(payload);
            alert('Data submitted successfully');
        } catch (error) {
            console.error('Error submitting data:', error);
            alert('Failed to submit data');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Barcode/RFID Inspection</h3>
            <form onSubmit={handleSubmit}>
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                        <thead>
                            <tr className="bg-[#424752] text-white text-center">
                                <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                                <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                                <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                                <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                                <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                            </tr>
                        </thead>
                        <tbody>
                            {barcodeRFIDData.map((item, index) => (
                                <tr
                                    key={index}
                                    className={`border-b last:border-none ${
                                        index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                                    } hover:bg-gray-100 transition-colors duration-200`}
                                >
                                    <td className="py-3 px-4">{item.product}</td>
                                    <td className="py-3 px-4">{item.specifications}</td>
                                    <td className="py-3 px-4">{item.frequency}</td>
                                    <td className="py-3 px-4">
                                        <input
                                            type="text"
                                            value={observations[index]?.observation1 || ''}
                                            onChange={(e) =>
                                                handleObservationChange(
                                                    index,
                                                    'observation1',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Enter observation 1"
                                            className={`w-full border p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150 ${
                                                validationErrors[index]
                                                    ? 'border-red-500'
                                                    : 'border-gray-300'
                                            }`}
                                            required
                                        />
                                    </td>
                                    <td className="py-3 px-4">
                                        <input
                                            type="text"
                                            value={observations[index]?.observation2 || ''}
                                            onChange={(e) =>
                                                handleObservationChange(
                                                    index,
                                                    'observation2',
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Enter observation 2"
                                            className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-6">
                    <label
                        className="block text-gray-700 font-semibold mb-2"
                        htmlFor="nonConformance"
                    >
                        Non Conformance
                    </label>
                    <textarea
                        id="nonConformance"
                        value={nonConformance}
                        onChange={handleNonConformanceChange}
                        placeholder="Enter non-conformance details"
                        className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                        rows="4"
                    ></textarea>
                </div>

                <div className="mt-6">
                    <button
                        type="submit"
                        className="bg-[#424752] text-white px-6 py-3 rounded-lg shadow-md hover:bg-gray-500 transition-all duration-150"
                    >
                        Submit
                    </button>
                </div>
            </form>
        </div>
    );
};

export default BarcodeRFID;